import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiExternalLink } from 'react-icons/fi';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBook, FiDatabase, FiZap, FiGlobe, FiShield, FiCpu } = FiIcons;

const appsList = [
  { id: 'apalfred', icon: FiBook, cat: 'apa' },
  { id: 'colombia', icon: FiDatabase, cat: 'repo' },
  { id: 'innovaprompt', icon: FiZap, cat: 'ai' },
  { id: 'lingualfred', icon: FiGlobe, cat: 'lang' },
  { id: 'plapi', icon: FiShield, cat: 'ethics' },
  { id: 'recursosia', icon: FiCpu, cat: 'ai' }
];

export default function Applications() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const categories = ['all', 'apa', 'repo', 'ai', 'lang', 'ethics'];

  const filtered = useMemo(() => {
    return appsList.filter(app => {
      const matchesSearch = t(`apps.items.${app.id}.title`).toLowerCase().includes(search.toLowerCase()) ||
                          t(`apps.items.${app.id}.desc`).toLowerCase().includes(search.toLowerCase());
      const matchesFilter = filter === 'all' || app.cat === filter;
      return matchesSearch && matchesFilter;
    });
  }, [search, filter, t]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-16">
        <h1 className="text-4xl md:text-5xl font-black dark:text-white">Aplicaciones</h1>
        
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          <div className="relative w-full sm:w-80">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text"
              placeholder={t('apps.search')}
              className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white dark:bg-white/5 border border-gray-200 dark:border-gray-800 outline-none focus:ring-2 focus:ring-primary shadow-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-4 mb-12 scrollbar-none snap-x">
        {categories.map(c => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`snap-start px-6 py-3 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${filter === c ? 'bg-primary text-white shadow-lg' : 'bg-white dark:bg-white/5 border border-gray-100 dark:border-gray-800'}`}
          >
            {c === 'all' ? t('apps.filterAll') : t(`apps.categories.${c}`).split(' ')[0]}
          </button>
        ))}
      </div>

      {/* Grid */}
      <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <AnimatePresence mode="popLayout">
          {filtered.map(app => (
            <motion.div
              layout
              key={app.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="group bg-white dark:bg-dark-surface p-8 rounded-[2.5rem] border border-gray-100 dark:border-gray-800 hover:border-primary/50 transition-all shadow-sm hover:shadow-2xl flex flex-col"
            >
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                <SafeIcon icon={app.icon} className="text-3xl text-primary" />
              </div>
              
              <div className="flex-1">
                <span className="text-xs font-black uppercase tracking-widest text-primary mb-2 block">
                  {t(`apps.categories.${app.cat}`)}
                </span>
                <h3 className="text-2xl font-extrabold mb-4 dark:text-white">{t(`apps.items.${app.id}.title`)}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                  {t(`apps.items.${app.id}.desc`)}
                </p>
              </div>

              <button className="w-full py-4 rounded-2xl bg-gray-50 dark:bg-white/5 group-hover:bg-primary text-gray-900 dark:text-white group-hover:text-white font-bold transition-all flex items-center justify-center gap-2 border border-gray-100 dark:border-gray-700 group-hover:border-primary">
                {t(`apps.items.${app.id}.btn`)}
                <FiExternalLink />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}